/* De delen die de lijsten van Organisaties en Contacten delen, in de opzet van de
 * projectlijst en Locaties:
 *
 *   /           zoeken
 *   ↑ ↓ (j k)   door de lijst
 *   Enter       openen (cmd-klik op een rij: nieuw tabblad)
 *   Escape      zoekterm wissen */

import { forwardRef, useEffect, type ReactNode, type RefObject } from 'react';
import { ArrowDown, ArrowUp, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export function useLijstToetsen({ zoekRef, aantal, actief, setActief, open, wis }: {
  zoekRef: RefObject<HTMLInputElement>;
  aantal: number;
  actief: number;
  setActief: (f: (a: number) => number) => void;
  open: (i: number) => void;
  wis: () => void;
}) {
  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      const doel = e.target as HTMLElement;
      const typen = doel.tagName === 'INPUT' || doel.tagName === 'TEXTAREA' || doel.isContentEditable;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!typen && e.key === '/') {
        e.preventDefault();
        zoekRef.current?.focus();
        return;
      }
      /* in een dialoog (bewerken) of een ander veld: niets */
      if (typen && doel !== zoekRef.current) return;
      if (doel.closest?.('[role="dialog"], [role="alertdialog"]')) return;
      if (e.key === 'ArrowDown' || (!typen && e.key === 'j')) {
        e.preventDefault();
        if (typen) zoekRef.current?.blur();
        setActief((a) => Math.min(aantal - 1, a + 1));
      } else if (e.key === 'ArrowUp' || (!typen && e.key === 'k')) {
        e.preventDefault();
        setActief((a) => Math.max(0, a - 1));
      } else if (e.key === 'Enter' && aantal) {
        if (typen || actief >= 0) {
          e.preventDefault();
          open(actief >= 0 ? actief : 0);
        }
      } else if (e.key === 'Escape' && typen) {
        wis();
        zoekRef.current?.blur();
      }
    };
    window.addEventListener('keydown', toets);
    return () => window.removeEventListener('keydown', toets);
  }, [zoekRef, aantal, actief, setActief, open, wis]);
}

export const Werkbalk = forwardRef<HTMLInputElement, {
  titel: string;
  zoek: string;
  onZoek: (z: string) => void;
  placeholder: string;
  nieuw?: { label: string; onKlik: () => void } | null;
}>(function Werkbalk({ titel, zoek, onZoek, placeholder, nieuw }, ref) {
  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      <h1 className="mr-2 text-2xl font-semibold tracking-tight">{titel}</h1>
      <label className="relative order-last w-full sm:order-none sm:ml-auto sm:w-80">
        <span className="sr-only">{placeholder}</span>
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input ref={ref} value={zoek} onChange={(e) => onZoek(e.target.value)} placeholder={placeholder} className="h-9 pl-8 pr-8" />
        <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border bg-muted px-1.5 font-mono text-[11px] text-muted-foreground sm:block">/</kbd>
      </label>
      {nieuw && (
        <Button size="sm" onClick={nieuw.onKlik} className="ml-auto h-9 sm:ml-0">
          <Plus className="mr-1.5 h-4 w-4" />
          <span className="hidden sm:inline">{nieuw.label}</span>
          <span className="sm:hidden">Nieuw</span>
        </Button>
      )}
    </div>
  );
});

export function Tabs<T extends string>({ tabs, label, actief, onKies, stip = {} }: {
  tabs: { tab: T; label: string; aantal: number }[];
  label: string;
  actief: T;
  onKies: (t: T) => void;
  stip?: Partial<Record<T, string>>;
}) {
  return (
    <div role="tablist" aria-label={label} className="-mx-3 flex w-[calc(100%+1.5rem)] gap-1.5 overflow-x-auto px-3 [scrollbar-width:none] sm:mx-0 sm:w-auto sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden">
      {tabs.map((t) => {
        const aan = actief === t.tab;
        return (
          <button
            key={t.tab}
            type="button"
            role="tab"
            aria-selected={aan}
            onClick={() => onKies(t.tab)}
            className={cn(
              'flex h-9 shrink-0 items-center gap-2 rounded-lg border px-3 text-sm transition-colors',
              aan ? 'border-foreground bg-foreground text-background' : 'bg-background hover:bg-muted',
            )}
          >
            {stip[t.tab] && <span className={cn('h-2 w-2 rounded-full', stip[t.tab])} aria-hidden />}
            <span className="font-medium">{t.label}</span>
            <span className={cn('tabular-nums', aan ? 'opacity-80' : 'text-muted-foreground')}>{t.aantal}</span>
          </button>
        );
      })}
    </div>
  );
}

export function SorteerKop<S extends string>({ s, huidig, oplopend, onSorteer, children }: {
  s: S;
  huidig: S;
  oplopend: boolean;
  onSorteer: (s: S) => void;
  children: ReactNode;
}) {
  return (
    <button type="button" onClick={() => onSorteer(s)} className={cn('inline-flex items-center gap-1 hover:text-foreground', huidig === s && 'font-semibold text-foreground')}>
      {children}
      {huidig === s && (oplopend ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
    </button>
  );
}

export function LijstStaat({ fout, laden, leeg, wat }: { fout: Error | null; laden: boolean; leeg: ReactNode; wat: string }) {
  if (fout) {
    return (
      <div role="alert" className="rounded-xl border border-destructive/40 px-5 py-6 text-sm">
        <p className="font-medium text-destructive">{wat} konden niet geladen worden.</p>
        <p className="text-muted-foreground">{fout.message}</p>
      </div>
    );
  }
  if (laden) {
    return (
      <div className="flex flex-col gap-2 rounded-xl border p-4" aria-busy>
        {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-9 animate-pulse rounded bg-muted" />)}
      </div>
    );
  }
  return <div className="rounded-xl border px-5 py-10 text-center text-sm text-muted-foreground">{leeg}</div>;
}

export function Voet({ aantal, enkelvoud, meervoud }: { aantal: number; enkelvoud: string; meervoud: string }) {
  return (
    <p className="border-t px-4 py-2 text-xs text-muted-foreground">
      {aantal} {aantal === 1 ? enkelvoud : meervoud} · <kbd className="font-mono">/</kbd> zoeken · <kbd className="font-mono">↑ ↓</kbd> bladeren · <kbd className="font-mono">Enter</kbd> openen
    </p>
  );
}

/** Het icoon van een organisatie (favicon of logo), met een letter als terugval. */
export function OrgIcoon({ src, naam, className }: { src: string | null; naam: string; className?: string }) {
  return (
    <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded bg-muted text-[11px] font-semibold text-muted-foreground', className)} aria-hidden>
      {src ? (
        <img src={src} alt="" className="h-full w-full object-contain" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      ) : (
        naam.charAt(0).toUpperCase()
      )}
    </span>
  );
}
